import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/**
 * Optional async thunk that simply returns the payload.
 * Use this if you want to reuse the pending/fulfilled/rejected flow
 * without calling any external API.
 *
 * Example usage:
 * dispatch(storeCustomerDetails(payload));
 */
export const storeCustomerDetails = createAsyncThunk(
  "getCustomerDetails/storeCustomerDetails",
  async (payload, { rejectWithValue }) => {
    try {
      // No API call — just return the payload so extraReducers can handle it
      return payload;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const initialState = {
  customerDetails: {
    CustomerNumber: "",
    BadDebtAmount: 0.0,
    SalesAgreements: [],
    InvoiceYears: [],
    SalesAgreementVer5PPEPList: [],
    Payments: [],
    PaymentYears: [],
    Donations: [],
  },
  loading: false,
  error: null,
};

const getCustomerDetailsSlice = createSlice({
  name: "getCustomerDetails",
  initialState,
  reducers: {
    // Synchronous setter: dispatch from component with the object to store
    setCustomerDetails: (state, action) => {
      state.customerDetails = { ...state.customerDetails, ...action.payload };
      state.error = null;
    },
    // Replace entire object
    replaceCustomerDetails: (state, action) => {
      state.customerDetails = action.payload;
      state.error = null;
    },
    clearCustomerDetails: (state) => {
      state.customerDetails = { ...initialState.customerDetails };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(storeCustomerDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(storeCustomerDetails.fulfilled, (state, action) => {
        state.loading = false;
        // Map or transform payload if needed; here we merge by default
        state.customerDetails = { ...state.customerDetails, ...action.payload };
      })
      .addCase(storeCustomerDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Storing customer details failed";
      });
  },
});

export const {
  setCustomerDetails,
  replaceCustomerDetails,
  clearCustomerDetails,
} = getCustomerDetailsSlice.actions;

export default getCustomerDetailsSlice.reducer;
